// Task interactions shared by the Tarefas page and the week view (Android Services/TaskActions.cs),
// and the row that shows one occurrence.
import React, { useCallback, useEffect, useRef } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';
import ChecklistIcon from '@mui/icons-material/Checklist';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import EventIcon from '@mui/icons-material/Event';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useNavigate } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { dayLabel, occurrenceSubtitle } from '../labels';
import { useNotify, useRun, useUndoable } from '../common';
import { SheetOption, useSheets } from './Sheet';

/** Fired after a task changes from a place that cannot reload the page showing it (an undo after leaving a page). */
export const TASKS_CHANGED = 'planner:tasks-changed';
const announce = () => window.dispatchEvent(new Event(TASKS_CHANGED));

/** Calls `reload` whenever tasks change elsewhere (see TASKS_CHANGED). */
export function useTasksChanged(reload: () => void) {
  const ref = useRef(reload);
  ref.current = reload;
  useEffect(() => {
    const handler = () => ref.current();
    window.addEventListener(TASKS_CHANGED, handler);
    return () => window.removeEventListener(TASKS_CHANGED, handler);
  }, []);
}

/** Where the steps of an occurrence are ticked. */
export const checklistPath = (o: T.TaskOccurrence) => `/casa/tarefas/passos/${o.publicId}?data=${o.date}`;

/** Subtitle as on Android: people, time, steps, ... and the notes on a second line. */
export function occurrenceText(o: T.TaskOccurrence, members: T.HouseholdMember[]): string {
  let text = occurrenceSubtitle(o, members);
  const notes = o.notes?.trim();
  if (notes) text += (text.length > 0 ? '\n' : '') + '📝 ' + (notes.length > 60 ? notes.substring(0, 60) + '…' : notes);
  return text;
}

export interface TaskActions {
  /** Ticks a pending occurrence (opening its steps when it has them) or reopens a done/skipped one. */
  toggle: (o: T.TaskOccurrence) => Promise<void>;
  /** What can be done with one task: done/reopen, steps, who does it this time, another day, edit, dismiss. */
  menu: (o: T.TaskOccurrence) => Promise<void>;
  /** Completes a pending occurrence, asking who did it when it is not obvious. False when cancelled or failed. */
  complete: (o: T.TaskOccurrence) => Promise<boolean>;
  /** Render this once in the page (the dialogs). */
  element: React.ReactElement;
}

/**
 * `reload` refreshes the page after a change (also after "Desfazer"). `changed`, when given, gets the occurrence as the
 * server left it first, so the page changes the row at once instead of waiting for the reload.
 */
export function useTaskActions(members: T.HouseholdMember[], reload: () => void | Promise<void>,
  changed?: (o: T.TaskOccurrence) => void): TaskActions {
  const sheets = useSheets();
  const notify = useNotify();
  const run = useRun();
  const offerUndo = useUndoable();
  const navigate = useNavigate();
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  const changedRef = useRef(changed);
  changedRef.current = changed;
  const show = useCallback((o: T.TaskOccurrence | undefined) => {
    if (o !== undefined) changedRef.current?.(o);
    return o;
  }, []);

  const reloadAll = useCallback(async () => {
    await reloadRef.current();
  }, []);
  useTasksChanged(reloadAll);
  // After a change: a page that shows the server's answer in the row (`changed`) needs no reload, since a change to
  // one occurrence makes no other; "Desfazer" still reloads
  const refresh = useCallback(async () => {
    if (!changedRef.current) await reloadRef.current();
  }, []);

  // Every page showing tasks reloads after "Desfazer" (this one too, or the one shown now)
  const offerReopen = useCallback((o: T.TaskOccurrence, message: string) =>
    offerUndo(message, () => api.reopenOccurrence(o.publicId), announce), [offerUndo]);

  /** Who did it, when not obvious (no assignee, or someone without an account). Null member: the server decides. */
  const askWhoDidIt = useCallback(async (o: T.TaskOccurrence): Promise<{ proceed: boolean; doneBy: T.Guid | null }> => {
    const assignee = members.find(m => m.publicId === o.assigneePublicId);
    if ((assignee && assignee.hasAccount) || members.length < 2) return { proceed: true, doneBy: null };
    const choice = await sheets.choose('Quem fez?', members.map(m => ({ value: m.publicId, text: m.name, icon: <PersonIcon /> })), o.title);
    const member = members.find(m => m.publicId === choice);
    return member ? { proceed: true, doneBy: member.publicId } : { proceed: false, doneBy: null };
  }, [members, sheets]);

  const complete = useCallback(async (o: T.TaskOccurrence) => {
    const { proceed, doneBy } = await askWhoDidIt(o);
    if (!proceed) return false;
    const result = show(await run(() => api.completeOccurrence(o.publicId, doneBy)));
    if (result === undefined) return false;
    offerReopen(o, `${o.title}: feita`);
    return true;
  }, [askWhoDidIt, run, offerReopen, show]);

  const toggle = useCallback(async (o: T.TaskOccurrence) => {
    if (o.status !== 'Pending') {
      if (show(await run(() => api.reopenOccurrence(o.publicId))) !== undefined) await refresh();
      return;
    }
    // Steps to tick first: the checklist completes the task from there
    if (o.checklist.length > 0) {
      navigate(checklistPath(o), { state: { occurrence: o } });
      return;
    }
    if (await complete(o)) await refresh();
  }, [run, refresh, navigate, complete, show]);

  const menu = useCallback(async (o: T.TaskOccurrence) => {
    const pending = o.status === 'Pending';
    const options: SheetOption[] = [
      pending
        ? { value: 'done', text: 'Marcar como feita', icon: <CheckIcon />, primary: true }
        : { value: 'done', text: 'Voltar a pôr por fazer', icon: <ReplayIcon /> },
    ];
    if (o.checklist.length > 0)
      options.push({ value: 'steps', text: 'Passos', icon: <ChecklistIcon />, detail: `${o.checklist.filter(i => i.isDone).length} de ${o.checklist.length} feitos` });
    if (pending) {
      options.push({ value: 'person', text: 'Quem faz', icon: <PersonIcon />, detail: 'Só desta vez' });
      options.push({
        value: 'day', text: 'Mudar o dia', icon: <EventIcon />,
        detail: o.source === 'ShoppingTrip' ? 'A ida às compras muda também' : dayLabel(o.date),
      });
    }
    options.push({
      value: 'edit', text: 'Editar', icon: <EditIcon />,
      detail: o.source === 'Manual' ? 'Nome, quando, quem faz, notas e passos' : 'Quem faz, notas e passos',
    });
    if (pending) options.push({ value: 'skip', text: 'Dispensar esta vez', icon: <CloseIcon /> });

    const subtitle = occurrenceText(o, members);
    const action = await sheets.choose(o.title, options, subtitle || null);
    switch (action) {
      case 'done':
        await toggle(o);
        return;
      case 'steps':
        navigate(checklistPath(o), { state: { occurrence: o } });
        return;
      case 'edit':
        navigate(`/casa/tarefas/${o.taskPublicId}`);
        return;
      case 'person': {
        const who = await sheets.choose('Quem faz?', [
          { value: 'anyone', text: 'Quem puder', detail: 'Ninguém em particular', icon: <GroupsIcon /> },
          ...members.map(m => ({ value: m.publicId, text: m.name, icon: <PersonIcon />, primary: m.publicId === o.assigneePublicId })),
        ], o.title);
        if (who === null) return;
        const member = members.find(m => m.publicId === who);
        const result = show(await run(() => api.rescheduleOccurrence(o.publicId, { date: o.date, dueTime: o.dueTime ?? null, assigneePublicId: member?.publicId ?? null })));
        if (result !== undefined) await refresh();
        return;
      }
      case 'day': {
        const date = await sheets.chooseDay('Para que dia?', o.date, o.title);
        if (date === null || date === o.date) return;
        const result = show(await run(() => api.rescheduleOccurrence(o.publicId, { date, dueTime: o.dueTime ?? null, assigneePublicId: o.assigneePublicId ?? null })));
        if (result === undefined) return;
        notify(`${o.title}: ${dayLabel(date).toLowerCase()}`);
        await refresh();
        return;
      }
      case 'skip': {
        const result = show(await run(() => api.skipOccurrence(o.publicId)));
        if (result === undefined) return;
        offerReopen(o, `${o.title}: dispensada`);
        await refresh();
        return;
      }
      default:
    }
  }, [members, sheets, toggle, navigate, run, refresh, notify, offerReopen, show]);

  return { toggle, menu, complete, element: sheets.element };
}

/** One occurrence: the circle ticks it; the text (or ⋮) opens what can be done with it. */
export function TaskRow({ occurrence: o, members, actions, dense }: {
  occurrence: T.TaskOccurrence; members: T.HouseholdMember[]; actions: TaskActions; dense?: boolean;
}) {
  const pending = o.status === 'Pending';
  const subtitle = occurrenceText(o, members);
  const glyph = o.status === 'Done'
    ? <CheckCircleIcon color="primary" />
    : o.status === 'Skipped' ? <RemoveCircleOutlineIcon color="disabled" /> : <RadioButtonUncheckedIcon color="primary" />;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', px: dense ? 0.5 : 1, py: dense ? 0.25 : 0.5, gap: 0.5 }}>
      <IconButton
        onClick={() => actions.toggle(o)}
        aria-label={pending ? `Marcar «${o.title}» como feita` : `Voltar a pôr «${o.title}» por fazer`}
        size={dense ? 'small' : 'medium'}
      >
        {glyph}
      </IconButton>
      <Box
        role="button" tabIndex={0}
        onClick={() => actions.menu(o)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); actions.menu(o); } }}
        sx={{ flexGrow: 1, minWidth: 0, cursor: 'pointer', py: 0.5 }}
      >
        <Typography
          variant={dense ? 'body2' : 'body1'}
          sx={{ color: pending ? (o.isOverdue ? '#E65100' : 'text.primary') : 'text.disabled', overflowWrap: 'anywhere' }}
        >
          {o.title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <IconButton size="small" onClick={() => actions.menu(o)} aria-label="Mais opções">
        <MoreVertIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}
